import { useState } from 'react';

import { CheckCircle2 } from 'lucide-react';
import PhoneInput, { isValidPhoneForCountry, parsePhoneDigits } from 'shared/ui/phone-input';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import {
  useLazyLookupD360ChannelQuery,
  useLazyLookupProjectByPhoneQuery,
  useUpdateProjectMutation,
} from '../api/master.api';

type Step = 'origin' | 'd360' | 'webhook';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  initialPhoneNumber?: string;
  onSave: (phoneNumber: string, channelId: string, apiKey: string) => Promise<void>;
  onConfigureWebhook: () => Promise<void>;
  isSaving?: boolean;
  isConfiguringWebhook?: boolean;
}

const ConfigureNumberDialog = ({
  open,
  onOpenChange,
  projectId,
  initialPhoneNumber = '',
  onSave,
  onConfigureWebhook,
  isSaving,
  isConfiguringWebhook,
}: Props) => {
  const [lookupD360Channel, { isFetching: isLookingUp }] = useLazyLookupD360ChannelQuery();
  const [lookupProjectByPhone, { isFetching: isVerifying }] = useLazyLookupProjectByPhoneQuery();
  const [updateProject, { isLoading: isReleasing }] = useUpdateProjectMutation();

  const normalizedInitial = initialPhoneNumber.replace(/\D/g, '');

  const [step, setStep] = useState<Step>('origin');
  const [phoneNumber, setPhoneNumber] = useState(normalizedInitial);
  const [d360ChannelId, setD360ChannelId] = useState('');
  const [d360ApiKey, setD360ApiKey] = useState('');
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [channelIdError, setChannelIdError] = useState<string | null>(null);
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [webhookError, setWebhookError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);
  const [conflictProject, setConflictProject] = useState<{ id: number; name: string } | null>(null);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [releaseConfirmDialogOpen, setReleaseConfirmDialogOpen] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setStep('origin');
      setPhoneNumber(normalizedInitial);
      setD360ChannelId('');
      setD360ApiKey('');
      setLookupError(null);
      setChannelIdError(null);
      setApiKeyError(null);
      setSaveError(null);
      setWebhookError(null);
      setVerified(false);
      setConflictProject(null);
      setConflictDialogOpen(false);
      setReleaseConfirmDialogOpen(false);
      setReleaseError(null);
    }
    onOpenChange(next);
  };

  const handleLookup = async () => {
    setLookupError(null);
    setChannelIdError(null);
    setApiKeyError(null);

    const result = await lookupD360Channel(phoneNumber);

    if (!result.data) {
      setLookupError(`No se encontró un canal 360dialog para +${phoneNumber}`);
      return;
    }

    const { channelId, apiKey } = result.data;

    if (channelId) {
      setD360ChannelId(channelId);
    } else {
      setChannelIdError('No se pudo obtener el Channel ID, escríbelo manualmente o espera unos segundos y reintenta.');
    }

    if (apiKey) {
      setD360ApiKey(apiKey);
    } else {
      setApiKeyError('No se pudo obtener el API Key, escríbelo manualmente o espera unos segundos y reintenta.');
    }
  };

  const handleVerify = async () => {
    setLookupError(null);
    setReleaseError(null);
    const result = await lookupProjectByPhone({
      phoneNumber,
      excludeProjectId: projectId,
    });
    const conflict = result.data?.project ?? null;
    if (!conflict) {
      setVerified(true);
      return;
    }
    setConflictProject(conflict);
    setConflictDialogOpen(true);
  };

  const handleConfirmRelease = async () => {
    if (!conflictProject) return;
    setReleaseError(null);
    const placeholder = `${phoneNumber}_used_by_project_id_${projectId}`;
    try {
      await updateProject({
        id: conflictProject.id,
        patch: {
          phoneNumber: placeholder,
          whatsAppProperties: null,
          whatsAppBusinessAccountId: null,
          whatsAppFromPhoneNumberId: null,
          whatsAppToken: null,
        },
      }).unwrap();
      setReleaseConfirmDialogOpen(false);
      setConflictDialogOpen(false);
      setVerified(true);
    } catch (e) {
      setReleaseError(extractErrorMessage(e, 'No se pudo liberar el número del otro proyecto.'));
    }
  };

  const handleConfirm = async () => {
    setSaveError(null);
    try {
      await onSave(phoneNumber, d360ChannelId, d360ApiKey);
      setStep('webhook');
    } catch (e) {
      setSaveError(
        e instanceof Error ? e.message : 'Error configurando número, inténtalo de nuevo más tarde por favor.'
      );
    }
  };

  const handleWebhookYes = async () => {
    setWebhookError(null);
    try {
      await onConfigureWebhook();
      handleOpenChange(false);
    } catch {
      setWebhookError('Error configurando el webhook, inténtalo de nuevo más tarde por favor.');
    }
  };

  const { country: countryCode, local } = parsePhoneDigits(phoneNumber);
  const isPhoneValid = isValidPhoneForCountry(phoneNumber, countryCode);
  const canSave = isPhoneValid && d360ChannelId.trim().length > 0 && d360ApiKey.trim().length > 0;
  const needsVerify = isPhoneValid && !verified;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {step === 'origin' && (
          <>
            <DialogHeader>
              <DialogTitle>Origen del número</DialogTitle>
            </DialogHeader>

            <div className="flex gap-3 pt-2">
              <Button className="flex-1" variant="outline" onClick={() => setStep('d360')}>
                360 Dialog
              </Button>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex-1">
                      <Button className="w-full" variant="outline" disabled>
                        Meta
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Configuración no disponible por el momento</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </>
        )}

        {step === 'd360' && (
          <>
            <DialogHeader>
              <DialogTitle>360 Dialog</DialogTitle>
            </DialogHeader>

            <div className="space-y-5 pt-2">
              <div className="space-y-1.5">
                <Label>Número de teléfono</Label>
                <div className="flex gap-2">
                  <PhoneInput
                    value={phoneNumber}
                    onChange={(digits) => {
                      setPhoneNumber(digits);
                      setVerified(false);
                    }}
                    className="flex-1"
                  />
                  {needsVerify ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isVerifying}
                      onClick={() => {
                        void handleVerify();
                      }}
                    >
                      {isVerifying ? 'Verificando...' : 'Verificar disponibilidad'}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!isPhoneValid || isLookingUp}
                      onClick={() => {
                        void handleLookup();
                      }}
                    >
                      {isLookingUp ? 'Buscando...' : 'Rellenar campos'}
                    </Button>
                  )}
                </div>
                {!isPhoneValid && local.length > 0 && (
                  <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                    Número de teléfono inválido para {countryCode}
                  </p>
                )}
                {verified && (
                  <p className="flex items-center gap-1.5 rounded-md border border-green-300 bg-green-50 p-2 text-sm text-green-800">
                    <CheckCircle2 className="h-4 w-4" />
                    Número disponible
                  </p>
                )}
                {lookupError && (
                  <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                    {lookupError}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="configure-d360-channel-id">Channel ID</Label>
                <Input
                  id="configure-d360-channel-id"
                  value={d360ChannelId}
                  onChange={(e) => {
                    setD360ChannelId(e.target.value);
                    setChannelIdError(null);
                  }}
                  disabled={needsVerify}
                />
                {channelIdError && (
                  <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                    {channelIdError}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="configure-d360-api-key">API Key</Label>
                <Input
                  id="configure-d360-api-key"
                  value={d360ApiKey}
                  onChange={(e) => {
                    setD360ApiKey(e.target.value);
                    setApiKeyError(null);
                  }}
                  disabled={needsVerify}
                />
                {apiKeyError && (
                  <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                    {apiKeyError}
                  </p>
                )}
              </div>

              {saveError && (
                <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                  {saveError}
                </p>
              )}

              <div className="flex justify-end pt-1">
                <Button
                  onClick={() => {
                    void handleConfirm();
                  }}
                  disabled={!canSave || isSaving || needsVerify}
                >
                  {isSaving ? 'Guardando...' : 'Confirmar'}
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 'webhook' && (
          <>
            <DialogHeader>
              <DialogTitle>Número configurado</DialogTitle>
            </DialogHeader>

            <div className="space-y-5 pt-2">
              {webhookError ? (
                <p className="text-sm text-text-muted">
                  El webhook no se pudo habilitar. Verifica que la API Key sea correcta y reintenta, o habilítalo más
                  tarde desde la pantalla del proyecto.
                </p>
              ) : (
                <p className="text-sm text-text-muted">
                  Se ha establecido el número. ¿Deseas configurar el webhook ahora?
                </p>
              )}

              {webhookError && (
                <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                  {webhookError}
                </p>
              )}

              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isConfiguringWebhook}>
                  {webhookError ? 'Habilitar luego' : 'No'}
                </Button>
                <Button
                  onClick={() => {
                    void handleWebhookYes();
                  }}
                  disabled={isConfiguringWebhook}
                >
                  {isConfiguringWebhook ? 'Configurando...' : webhookError ? 'Reintentar' : 'Sí'}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>

      <Dialog open={conflictDialogOpen} onOpenChange={setConflictDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Número en uso</DialogTitle>
          </DialogHeader>
          {conflictProject && (
            <p className="text-sm text-text-muted">
              El número <strong>+{phoneNumber}</strong> ya está asignado al proyecto{' '}
              <strong>{conflictProject.name}</strong> (id {conflictProject.id}). Podés liberarlo manualmente (editando
              el otro proyecto) o reasignarlo automáticamente desde acá.
            </p>
          )}
          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setConflictDialogOpen(false)}>
              Lo modificaré manualmente
            </Button>
            <Button
              onClick={() => {
                setConflictDialogOpen(false);
                setReleaseConfirmDialogOpen(true);
              }}
            >
              Modificar automáticamente
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={releaseConfirmDialogOpen} onOpenChange={setReleaseConfirmDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar reasignación</DialogTitle>
          </DialogHeader>
          {conflictProject && (
            <div className="space-y-2 text-sm">
              <p className="text-text-muted">Se va a actualizar el proyecto:</p>
              <ul className="space-y-1 rounded-md border border-border bg-muted/30 p-3">
                <li>
                  <span className="text-text-muted">Nombre:</span> <strong>{conflictProject.name}</strong>
                </li>
                <li>
                  <span className="text-text-muted">ID:</span> {conflictProject.id}
                </li>
                <li>
                  <span className="text-text-muted">Número actual:</span> {phoneNumber}
                </li>
                <li className="break-all">
                  <span className="text-text-muted">Nuevo número:</span> {phoneNumber}_used_by_project_id_{projectId}
                </li>
              </ul>
              <p className="text-text-muted">
                Sus credenciales de WhatsApp se limpiarán y el número quedará libre para asignar acá.
              </p>
            </div>
          )}
          {releaseError && (
            <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {releaseError}
            </p>
          )}
          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setReleaseConfirmDialogOpen(false)} disabled={isReleasing}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                void handleConfirmRelease();
              }}
              disabled={isReleasing}
            >
              {isReleasing ? 'Reasignando...' : 'Confirmar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ConfigureNumberDialog;
